import './NotificationsDrawer.scss';

import {BellOutlined} from '@ant-design/icons';
import {Empty, Typography} from 'antd';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Virtuoso} from 'react-virtuoso';

import MobileNotificationItem from '../../../components/UserNotifications/NotificationItem/MobileNotificationItem';
import NotificationItem from '../../../components/UserNotifications/NotificationItem/NotificationItem';
import MobileNotificationTagFilter from '../../../components/UserNotifications/NotificationTagFilter/MobileNotificationTagFilter';
import NotificationTagFilter from '../../../components/UserNotifications/NotificationTagFilter/NotificationTagFilter';
import {getTagInfo} from '../../../consts/notifications/notifications';
import useMobileNotificationFeedback from '../../../hooks/useMobileNotificationFeedback';
import {handleMarkAsRead, makeNotificationsSelector, onReadAllClick} from '../../../state/ducks/notifications';

const NotificationsDrawerContent = ({panelRef, mobile = false}) => {
    const dispatch = useDispatch();
    const {notifications, error} = useSelector(makeNotificationsSelector());
    const [selectedTag, setSelectedTag] = useState(null);
    const contentRef = useRef(null);
    const [mobileFeedback, feedbackHolder] = useMobileNotificationFeedback(contentRef);
    const feedback = mobile ? mobileFeedback : undefined;
    const setPanelRef = useCallback(
        (node) => {
            contentRef.current = node;
            panelRef?.(node);
        },
        [panelRef]
    );

    const handleTagClick = useCallback((tag) => {
        setSelectedTag((prevTag) => (prevTag === tag ? null : tag));
    }, []);

    const categoryCounts = useMemo(() => {
        const counts = {};

        for (const item of notifications || []) {
            const {name} = getTagInfo(item.typeCode);

            counts[name] = (counts[name] || 0) + 1;
        }

        return counts;
    }, [notifications]);

    const filteredNotifications = useMemo(() => {
        if (!notifications) return [];
        if (selectedTag === null) return notifications;

        return notifications.filter((item) => {
            const {name} = getTagInfo(item.typeCode);

            return name === selectedTag;
        });
    }, [notifications, selectedTag]);

    const onMarkAsRead = useCallback((id) => dispatch(handleMarkAsRead(id, feedback)), [dispatch, feedback]);

    const handleReadAllClick = useCallback(() => {
        dispatch(onReadAllClick(feedback));
    }, [dispatch, feedback]);

    if (!mobile) {
        if (error) {
            return (
                <div className='p-4 text-center'>
                    <Typography.Text type='danger'>Помилка завантаження сповіщень.</Typography.Text>
                </div>
            );
        }

        if (!notifications?.length) {
            return <Empty description='У вас немає сповіщень.' className='mt-40' />;
        }

        return (
            <div id='notifications-panel' ref={panelRef} className='h-full flex flex-col'>
                <NotificationTagFilter
                    selectedTag={selectedTag}
                    categoryCounts={categoryCounts}
                    onTagClick={handleTagClick}
                    onReadAllClick={handleReadAllClick}
                />

                <div className='flex-1 h-full p-2 overflow-hidden'>
                    {filteredNotifications.length === 0 ? (
                        <Empty description='У вас немає сповіщень з обраним тегом.' className='mt-40' />
                    ) : (
                        <Virtuoso
                            className='notifications-scroll-container'
                            style={{height: '100%'}}
                            data={filteredNotifications}
                            itemContent={(index, item) => (
                                <NotificationItem key={item.id} item={item} onMarkAsRead={onMarkAsRead} />
                            )}
                        />
                    )}
                </div>
            </div>
        );
    }

    return (
        <div id='notifications-panel' ref={setPanelRef} className='notifications-content'>
            {feedbackHolder}
            {error ? (
                <div className='notifications-state'>
                    <Typography.Text type='danger'>Помилка завантаження сповіщень.</Typography.Text>
                </div>
            ) : !notifications?.length ? (
                <div className='notifications-state'>
                    <Empty image={<BellOutlined />} description='У вас немає сповіщень.' />
                </div>
            ) : (
                <>
                    <MobileNotificationTagFilter
                        selectedTag={selectedTag}
                        categoryCounts={categoryCounts}
                        totalCount={notifications.length}
                        onTagChange={setSelectedTag}
                        onReadAllClick={handleReadAllClick}
                    />

                    <div className='notifications-list'>
                        {filteredNotifications.length === 0 ? (
                            <div className='notifications-state'>
                                <Empty image={<BellOutlined />} description='У вас немає сповіщень з обраним тегом.' />
                            </div>
                        ) : (
                            <Virtuoso
                                className='notifications-scroll-container'
                                style={{height: '100%'}}
                                data={filteredNotifications}
                                itemContent={(index, item) => (
                                    <MobileNotificationItem key={item.id} item={item} onMarkAsRead={onMarkAsRead} />
                                )}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default NotificationsDrawerContent;
